import { useEffect, useState } from "react";
import { letTapsThroughBootLoader, removeBootLoader } from "../../components/loading/bootLoaderUtility";
import {
  ApiHelperAppContext,
  ApiHelperAppContextValue,
} from "./ApiHelperAppContext";

// A little longer than the loader's 300 ms delay, so a request that starts right after the first
// render shows <Loading /> (which takes over) before the boot loader would go.
const BOOT_LOADER_IDLE_MILLISECONDS = 350;

interface IApiHelperAppContextProviderProps {
  children: React.ReactNode;
}

export const ApiHelperAppContextProvider = ({
  children,
}: IApiHelperAppContextProviderProps) => {
  const [counter, setLoadingCount] = useState(0);
  const [isLoading, setIsLoadingVisible] = useState(false);

  const setIsLoading = (active: boolean) => {
    setLoadingCount((prev) => {
      const newCount = active ? prev + 1 : Math.max(0, prev - 1);
      return newCount;
    });
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (counter > 0 && !isLoading) {
      timeout = setTimeout(() => {
        setIsLoadingVisible((prev) => (counter > 0 && !prev ? true : prev));
      }, 300);
    }

    if (counter === 0 && isLoading) {
      setIsLoadingVisible(false);
    }

    return () => clearTimeout(timeout);
  }, [counter, isLoading]);

  // index.html's boot loader stays until the app is up: while requests run, <Loading /> takes
  // over from it; once nothing has loaded for a moment, it goes. The app has rendered by now, so
  // taps already go through it to the page.
  useEffect(letTapsThroughBootLoader, []);

  useEffect(() => {
    if (counter > 0) return;

    const timeout = setTimeout(removeBootLoader, BOOT_LOADER_IDLE_MILLISECONDS);
    return () => clearTimeout(timeout);
  }, [counter]);

  const contextValue: ApiHelperAppContextValue = {
    isLoading,
    setIsLoading,
  };

  return (
    <ApiHelperAppContext.Provider value={contextValue}>
      {children}
    </ApiHelperAppContext.Provider>
  );
};
